import { useState, useEffect } from 'react';
import { CopySimple, CheckCircle, Warning } from '@phosphor-icons/react';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DuplicatesPage() {
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDuplicates = () => {
    fetch(`${API}/duplicates`, { credentials: 'include' })
      .then(r => r.json())
      .then(setDuplicates)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchDuplicates(); }, []);

  const resolveDuplicate = async (dupId, action) => {
    await fetch(`${API}/duplicates/${dupId}/resolve`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action })
    });
    fetchDuplicates();
  };

  return (
    <div className="animate-fade-in" data-testid="duplicates-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tight">Duplicate Resolution</h1>
        <p className="text-sm text-zinc-500 mt-1">Review and resolve potential duplicate records</p>
      </div>

      <div className="space-y-4" data-testid="duplicates-list">
        {loading ? (
          <div className="border border-zinc-200 bg-white p-12 text-center text-zinc-400">Loading...</div>
        ) : duplicates.length === 0 ? (
          <div className="border border-zinc-200 bg-white p-12 text-center">
            <CheckCircle size={32} className="text-emerald-500 mx-auto mb-2" />
            <span className="text-emerald-600 text-sm font-medium">No duplicates to resolve</span>
          </div>
        ) : (
          duplicates.map(d => (
            <div key={d.duplicate_id} data-testid={`duplicate-card-${d.duplicate_id}`} className="border border-zinc-200 bg-white p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <CopySimple size={20} weight="bold" className="text-amber-500" />
                  <div>
                    <span className="font-data text-xs text-zinc-500">{d.duplicate_id}</span>
                    <div className="text-sm font-medium mt-0.5">{d.details}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`rounded-full px-2 py-0.5 text-xs font-data ${d.status === 'resolved' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                    {d.status}
                  </Badge>
                  <span className={`font-data text-sm font-bold ${d.confidence >= 0.8 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {(d.confidence * 100).toFixed(0)}% match
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-zinc-500 mb-4">
                <span>Match type: <span className="font-data">{d.match_type}</span></span>
                <span>A: <span className="font-data">{d.facility_id_a}</span></span>
                <span>B: <span className="font-data">{d.facility_id_b}</span></span>
              </div>
              {d.status === 'pending' && (
                <div className="flex items-center gap-2">
                  <button data-testid={`merge-${d.duplicate_id}`} onClick={() => resolveDuplicate(d.duplicate_id, 'merge')} className="px-4 py-2 text-xs font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 transition-colors">Merge Records</button>
                  <button data-testid={`keep-both-${d.duplicate_id}`} onClick={() => resolveDuplicate(d.duplicate_id, 'keep_both')} className="px-4 py-2 text-xs font-medium border border-zinc-200 rounded-sm hover:bg-zinc-50 transition-colors">Keep Both</button>
                  <button data-testid={`dismiss-${d.duplicate_id}`} onClick={() => resolveDuplicate(d.duplicate_id, 'dismiss')} className="px-4 py-2 text-xs font-medium text-zinc-500 hover:text-zinc-800 transition-colors">Dismiss</button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
